import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Loader2,
  Mail,
  Phone,
  Globe,
  Calendar,
  User,
  Building2,
} from 'lucide-react'
import { apiService } from '@/services/api'
import VaultProgress from '@/components/VaultProgress'
import DonateModal from '@/components/DonateModal'
import type { BalanceResponse } from '@/types'

// Renders plain text with [label](https://...) markdown-style links turned into anchors.
function renderWithLinks(text: string) {
  const parts: (string | JSX.Element)[] = []
  const pattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g
  let last = 0
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index))
    parts.push(
      <a
        key={match.index}
        href={match[2]}
        target="_blank"
        rel="noopener noreferrer"
        className="text-bitcoin hover:text-bitcoin-light underline"
      >
        {match[1]}
      </a>
    )
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push(text.slice(last))
  return parts
}

export default function ReserveDetail() {
  const { slug } = useParams<{ slug: string }>()
  const [showDonate, setShowDonate] = useState(false)
  const [balance, setBalance] = useState<BalanceResponse | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['reserve', slug],
    queryFn: () => apiService.getReserve(slug!),
    enabled: !!slug,
  })

  const reserve = data?.data

  useEffect(() => {
    if (!reserve || !slug) return

    const fetchBalance = async () => {
      try {
        const balanceResponse = await apiService.getReserveBalance(slug)
        setBalance(balanceResponse.data)
      } catch {
        // No wallet config or error — balance stays at 0
      }
    }

    fetchBalance()
  }, [reserve, slug])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="animate-spin text-bitcoin" size={40} />
        <span className="ml-3 text-gray-300 text-lg">Loading reserve...</span>
      </div>
    )
  }

  if (error || !reserve) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-red-400 text-lg mb-4">Reserve not found</p>
        <Link to="/" className="text-bitcoin hover:text-bitcoin-light">
          Back to home
        </Link>
      </div>
    )
  }

  const statusColor = (status: string) => {
    if (status === 'active') return 'bg-green-600'
    if (status === 'completed') return 'bg-blue-600'
    return 'bg-gray-600'
  }

  return (
    <div className="bg-gray-950 min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-bitcoin mb-6 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to all reserves
        </Link>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-5 mb-3">
            {reserve.logo_url && (
              <img
                src={reserve.logo_url}
                alt={`${reserve.name} logo`}
                className="w-20 h-20 object-contain flex-shrink-0"
              />
            )}
            <div>
              <h1 className="text-4xl font-bold text-white mb-1">{reserve.name}</h1>
              <p className="text-gray-400 text-lg">
                {reserve.city}, {reserve.state}
                {reserve.date_established && (
                  <span className="ml-3 text-gray-500">
                    Est. {parseInt(reserve.date_established.slice(0, 4), 10)}
                  </span>
                )}
              </p>
            </div>
          </div>
          {reserve.mission && (
            <p className="text-gray-300 mt-3 text-lg max-w-3xl">{reserve.mission}</p>
          )}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content (2 cols) */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {reserve.description && (
              <section className="bg-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">About</h2>
                <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {reserve.description}
                </div>
              </section>
            )}

            {/* Leadership */}
            {reserve.leaders.length > 0 && (
              <section className="bg-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Leadership</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {reserve.leaders.map((leader) => (
                    <div
                      key={leader.id}
                      className="bg-gray-700 rounded-lg p-4 flex items-start gap-4"
                    >
                      {leader.photo_url ? (
                        <img
                          src={leader.photo_url}
                          alt={leader.name}
                          className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0">
                          <User className="text-gray-400" size={24} />
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-white">{leader.name}</div>
                        {leader.title && (
                          <div className="text-bitcoin text-sm">{leader.title}</div>
                        )}
                        {leader.bio && (
                          <div className="text-gray-400 text-sm mt-1">{leader.bio}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Initiatives */}
            {reserve.initiatives.length > 0 && (
              <section className="bg-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Initiatives</h2>
                <div className="space-y-4">
                  {reserve.initiatives.map((initiative) => (
                    <div
                      key={initiative.id}
                      className="bg-gray-700 rounded-lg p-4"
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-white">{initiative.title}</h3>
                        <span
                          className={`${statusColor(initiative.status)} text-white text-xs px-2 py-0.5 rounded-full capitalize`}
                        >
                          {initiative.status}
                        </span>
                      </div>
                      {initiative.description && (
                        <p className="text-gray-400 text-sm">{initiative.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Timeline */}
            {reserve.timeline_events.length > 0 && (
              <section className="bg-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Timeline</h2>
                <div className="relative pl-6 border-l-2 border-bitcoin space-y-6">
                  {[...reserve.timeline_events]
                    .sort((a, b) => b.event_date.localeCompare(a.event_date))
                    .map((event) => (
                    <div key={event.id} className="relative">
                      <div className="absolute -left-[25px] w-3 h-3 rounded-full bg-bitcoin" />
                      <div className="text-bitcoin text-sm font-medium">
                        <Calendar className="inline mr-1" size={14} />
                        {new Date(event.event_date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          timeZone: 'UTC',
                        })}
                      </div>
                      <div className="font-semibold text-white mt-1">{event.title}</div>
                      {event.description && (
                        <p className="text-gray-400 text-sm mt-1 break-words">{renderWithLinks(event.description)}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Gallery */}
            {reserve.images?.length > 0 && (
              <section className="bg-gray-800 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-white mb-4">Gallery</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {reserve.images.map((image) => {
                    const content = (
                      <figure key={image.id}>
                        <img
                          src={image.image_url}
                          alt={image.caption || reserve.name}
                          className="w-full rounded-lg object-cover"
                        />
                        {image.caption && (
                          <figcaption className="text-gray-400 text-sm mt-2">
                            {image.caption}
                          </figcaption>
                        )}
                      </figure>
                    )
                    return image.link_url ? (
                      <a
                        key={image.id}
                        href={image.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:opacity-90 transition-opacity"
                      >
                        {content}
                      </a>
                    ) : (
                      content
                    )
                  })}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <VaultProgress
              confirmedBalanceSats={balance?.confirmed_balance_sats ?? null}
              goalBtc={reserve.vault_goal_btc}
              onDonate={() => setShowDonate(true)}
            />

            {reserve.legal_entities?.length > 0 && (
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Structure</h3>
                <div className="space-y-3">
                  {reserve.legal_entities.map((entity) => (
                    <div key={entity.id} className="flex items-start gap-3">
                      <Building2 size={18} className="text-bitcoin flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="text-white font-medium">{entity.name}</div>
                        {entity.entity_type && (
                          <div className="text-gray-400 text-sm">{entity.entity_type}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contact Info */}
            {(reserve.contact_email || reserve.contact_phone || reserve.website) && (
              <div className="bg-gray-800 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Contact</h3>
                <div className="space-y-3">
                  {reserve.contact_email && (
                    <a
                      href={`mailto:${reserve.contact_email}`}
                      className="flex items-center gap-3 text-gray-300 hover:text-bitcoin transition-colors"
                    >
                      <Mail size={18} className="flex-shrink-0" />
                      <span className="text-sm break-all">{reserve.contact_email}</span>
                    </a>
                  )}
                  {reserve.contact_phone && (
                    <a
                      href={`tel:${reserve.contact_phone}`}
                      className="flex items-center gap-3 text-gray-300 hover:text-bitcoin transition-colors"
                    >
                      <Phone size={18} className="flex-shrink-0" />
                      <span className="text-sm">{reserve.contact_phone}</span>
                    </a>
                  )}
                  {reserve.website && (
                    <a
                      href={reserve.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-300 hover:text-bitcoin transition-colors"
                    >
                      <Globe size={18} className="flex-shrink-0" />
                      <span className="text-sm break-all">{reserve.website}</span>
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showDonate && (
        <DonateModal
          reserveSlug={slug!}
          reserveName={reserve.name}
          onClose={() => setShowDonate(false)}
        />
      )}
    </div>
  )
}
